/* eslint-disable no-param-reassign */
import { Maybe } from "graphql/jsutils/Maybe";
import { FDR_LINK, USGSA_TRACK_SOURCE } from "@/constants/horseRacing";
import states from "@/constants/states";
import {
  Hr_BettingInterest,
  Hr_PastTrackLocation,
  Hr_Runner,
} from "@/graphql/gql/graphql";
import { timeUntil } from "@/utils/date";
import { filterEmpty } from ".";

export function getRunnerPick(
  runner: Hr_Runner,
  trackDataSource: string,
  provider = null
) {
  const pickProvider =
    provider ??
    (trackDataSource === USGSA_TRACK_SOURCE ? "timeform" : "handicapping");
  const { number, info } =
    runner?.[pickProvider]?.freePick ?? runner?.handicapping?.freePick ?? {};
  return { number, info };
}

export function sortByPick(
  bettingInterests: Hr_BettingInterest[],
  trackDataSource: string
) {
  return filterEmpty(bettingInterests).sort(
    ({ runners: runnersA }, { runners: runnersB }) => {
      const [runnerA] = runnersA || [];
      const [runnerB] = runnersB || [];
      if (!(runnerA && runnerB)) {
        return 0;
      }
      return (
        (getRunnerPick(runnerA, trackDataSource)?.number ?? 10) -
        (getRunnerPick(runnerB, trackDataSource)?.number ?? 10)
      );
    }
  );
}
export function handleize(name: string) {
  return name
    .toLowerCase()
    .replace(/[^\w\u00C0-\u024f]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
export function getFDRLink(trackCode = "", trackName = "", number = "") {
  return `${FDR_LINK}/racetracks/${trackCode}/${handleize(trackName)}${number ? `?race=${number}` : ""}`;
}

const LABELS: Record<number, string> = {
  1: "1st Pick",
  2: "2nd Pick",
  3: "3rd Pick",
  4: "4th Pick",
};
const SHORT_LABELS: Record<number, string> = {
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
};

export function getPickLabel(pick: number | undefined, short = false) {
  if (short) {
    return pick ? SHORT_LABELS[pick] || "" : "";
  }
  return pick ? LABELS[pick] || "" : "";
}

function shuffle<A>(a: A[]) {
  let i;
  let j;
  let x;

  for (i = a.length - 1; i > 0; i -= 1) {
    j = Math.floor(Math.random() * (i + 1));
    x = a[i];
    a[i] = a[j];
    a[j] = x;
  }
  return a;
}

export function getRandomLearningTerms<A = unknown>(terms: A[], number = 1) {
  return shuffle(terms).slice(0, number);
}
export function calcPercentage(total = 1, partial = 0) {
  return ((100 * partial) / total).toFixed();
}

export function calcWinProbability(numerator = 0, denominator = 1) {
  return `${Math.ceil((1 / (1 + numerator / (denominator || 1))) * 100)}%`;
}

export function makeSnakeCase(stringToConvert: Maybe<string> | undefined) {
  return stringToConvert?.toLowerCase().replaceAll(" ", "_");
}

export default function formatNumbersInThousands(num: number) {
  if (Math.abs(num) > 999) {
    const thousands = Math.sign(num) * (Math.abs(num) / 1000);
    return `${thousands.toFixed(1)}K`;
  }
  return (Math.sign(num) * Math.abs(num)).toString();
}

export function getLocation(location: Hr_PastTrackLocation) {
  const { city, state, country } = location || {};

  const _city = city?.trim();
  const _state = state?.trim();
  const _country = country?.trim();

  if (_country === "USA") {
    if (_city && _state) {
      return `${_city}, ${states[_state]}, ${_country}`;
    }

    if (_city) {
      return `${_city}, ${_country}`;
    }

    if (_state) {
      return `${states[_state]}, ${_country}`;
    }
  }

  return `${_country}`;
}

export function hasRaceStarted(postTime: string | undefined | null): boolean {
  return !!(postTime && timeUntil(postTime) <= 0);
}
